import { useMutation } from '@tanstack/react-query';
import {
  OrchestratorResourcesBody,
  ResourceReservationParams,
  SwitchClinicalSpecialistBody,
  SwitchConsoleBody,
} from '@/api/apis/orchestrator';
import {
  bookCaseResource,
  bookSpecificCaseResource,
  removeHospitalContact,
  removeOpoContact,
  removeCustomer,
  removeHospital,
  removeHub,
  removeOpo,
  removeReservedCaseResource,
  removeTxCenter,
  reserveCaseResource,
  switchClinicalSpecialist,
  switchOcsConsole,
  transferResources,
} from '@/queries/orchestrator/orchestrator.fetcher';
import { Uuid } from '@/api/types';
import { txCentersQueryKeys } from '@/queries/tx-centers/tx-centers.keys';
import { hospitalsQueryKeys } from '@/queries/hospital/hospital.keys';
import { hospitalContactsQueryKeys } from '@/queries/hospital-contacts';
import { customerQueryKeys } from '@/queries/customer/customer.keys';
import { hubsQueryKeys } from '@/queries/hub/hub.keys';
import { queryClient } from '@/queries';
import { api } from '@/api';
import { updateCaseCache } from '../case';
import { opoContactQueryKeys, opoQueryKeys } from '@/queries/opo/opo.keys';

export const useReserveResource = () => {
  return useMutation({
    mutationFn: (data: Partial<OrchestratorResourcesBody> & ResourceReservationParams) => reserveCaseResource(data),
  });
};

export const useRemoveReserveResource = () => {
  return useMutation({
    mutationFn: (data: Partial<OrchestratorResourcesBody> & ResourceReservationParams) =>
      removeReservedCaseResource(data),
  });
};

export const useBookResources = () => {
  return useMutation({
    mutationFn: ({ caseId }: ResourceReservationParams) => bookCaseResource({ caseId }),
  });
};

export const useBookSpecificResource = () => {
  return useMutation({
    mutationFn: (data: Partial<OrchestratorResourcesBody> & ResourceReservationParams) =>
      bookSpecificCaseResource(data),
  });
};

export const useSwitchOcsConsole = () => {
  return useMutation({
    mutationFn: ({ caseId, previousOcsConsoleId, ocsConsoleId }: ResourceReservationParams & SwitchConsoleBody) =>
      switchOcsConsole({ caseId, previousOcsConsoleId, ocsConsoleId }),
  });
};

export const useSwitchClinicalSpecialist = () => {
  return useMutation({
    mutationFn: ({
      caseId,
      previousResourceId,
      resourceId,
    }: ResourceReservationParams & SwitchClinicalSpecialistBody) =>
      switchClinicalSpecialist({ caseId, previousResourceId, resourceId }),
  });
};

type RemoveTXCenterMutationVars = {
  id: string;
};

export const useRemoveTXCenterMutation = () => {
  return useMutation({
    mutationFn: (variables: RemoveTXCenterMutationVars) => removeTxCenter(variables.id),
    meta: {
      invalidates: [txCentersQueryKeys.getList(), txCentersQueryKeys.getInfiniteList()],
    },
  });
};

type RemoveHospitalMutationVars = {
  id: string;
};

export const useRemoveHospitalMutation = () => {
  return useMutation({
    mutationFn: (variables: RemoveHospitalMutationVars) => removeHospital(variables.id),
    meta: {
      invalidates: [hospitalsQueryKeys.getList(), hospitalsQueryKeys.getInfiniteList()],
    },
  });
};

type RemoveOpoMutationVars = {
  id: string;
};

export const useRemoveOpoMutation = () => {
  return useMutation({
    mutationFn: (variables: RemoveOpoMutationVars) => removeOpo(variables.id),
    meta: {
      invalidates: [opoQueryKeys.getList(), opoQueryKeys.getInfiniteList()],
    },
  });
};

type RemoveContactMutationVars = {
  id: string;
};

export const useRemoveHospitalContactMutationWithCorrelation = () => {
  return useMutation({
    mutationFn: ({ id }: RemoveContactMutationVars) => removeHospitalContact({ id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hospitalContactsQueryKeys.getList(), exact: false });
    },
  });
};

export const useRemoveOpoContactMutation = () => {
  return useMutation({
    mutationFn: ({ id }: RemoveContactMutationVars) => removeOpoContact({ id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: opoContactQueryKeys.getList(), exact: false });
    },
  });
};

export const useRemoveCustomerMutationWithCorrelation = () => {
  return useMutation({
    mutationFn: (variables: { id: string }) => removeCustomer(variables.id),
    meta: {
      invalidates: [customerQueryKeys.getList(), customerQueryKeys.getInfiniteList()],
    },
  });
};

type RemoveHubMutationVars = {
  id: string;
};

export const useRemoveHubMutation = () => {
  return useMutation({
    mutationFn: (variables: RemoveHubMutationVars) => removeHub(variables.id),
    meta: {
      invalidates: [hubsQueryKeys.getList(), hubsQueryKeys.getInfiniteList()],
    },
  });
};

export const useTransferResourcesMutation = () => {
  return useMutation({
    mutationFn: ({ fromCaseId, toCaseId }: { fromCaseId: Uuid; toCaseId: Uuid }) =>
      transferResources(fromCaseId, toCaseId),
  });
};

export const useTransferReallocationData = () => {
  return useMutation({
    mutationFn: ({ fromCaseId, toCaseId }: { fromCaseId: Uuid; toCaseId: Uuid }) =>
      api.orchestrator.transferReallocationData(fromCaseId, toCaseId),
    onSuccess: updateCaseCache,
  });
};
